import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { signOut, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { API_ENDPOINT, PROFILE_ENDPOINT, SESSIONS_ENDPOINT, DAILY_USAGE_ENDPOINT, STREAKS_ENDPOINT, SESSION_END_ENDPOINT, FEEDBACK_ENDPOINT } from '../config/api';
import OnboardingModal from './OnboardingModal';
import ProfileSettings from './ProfileSettings';
import TermsAcceptanceModal from './TermsAcceptanceModal';
import NewChatDropdown from './NewChatDropdown';
import EndChatConfirmModal from './EndChatConfirmModal';
import JournalEntryCard from './JournalEntryCard';
import { LIFE_ANALYSIS_SECTIONS, LifeAnalysisIcons } from '../constants/lifeAnalysisSections';
import { getChatSessionsCache, setChatSessionsCache } from '../utils/cache';
import './ChatLayout.css';

const VIEW_MODE_STORAGE_KEY = 'life-journal-view-mode';

function getLocalDateString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Relative chat date label ("Today"/"Yesterday"/"3 Jul"). Timestamps are full ISO datetimes,
// so new Date() already localizes them correctly.
function formatChatDate(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '';
  const startOfDay = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return `${d.getDate()} ${d.toLocaleDateString('en-US', { month: 'short' })}`;
}

function ChatLayout({ onLogout }) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(true);
  const [showChats, setShowChats] = useState(true);
  const [showOlderChats, setShowOlderChats] = useState(false);
  const [lastFailedMessage, setLastFailedMessage] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpenOnMobile, setSidebarOpenOnMobile] = useState(false);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  const [userId, setUserId] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [messageFeedback, setMessageFeedback] = useState({});
  const [messageIds, setMessageIds] = useState([]);
  const [remainingTime, setRemainingTime] = useState(5400); // 90 min in seconds
  const [journalMode, setJournalMode] = useState(false);
  const [isJournalMode, setIsJournalMode] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [showNewChatDropdown, setShowNewChatDropdown] = useState(false);
  const [showEndChatConfirm, setShowEndChatConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'ai-chat' or 'journal'
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);

  // View mode is purely visual (chat bubbles vs journal entry cards) - independent of
  // session type (isJournalMode). Toggling it never touches timer/session state.
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'journal' ? 'journal' : 'chat';
    } catch {
      return 'chat';
    }
  });
  // Journal session's generated reflection image (session-level, not per-message)
  const [sessionImage, setSessionImage] = useState(null); // { status, url } | null

  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const newChatButtonRef = useRef(null);

  // Load user ID, profile, chats, and daily usage on mount (usage in loadUserData so timer is correct after refresh)
  useEffect(() => {
    loadUserData();
  }, []);

  const toggleViewMode = () => {
    setViewMode(prev => {
      const next = prev === 'chat' ? 'journal' : 'chat';
      try {
        localStorage.setItem(VIEW_MODE_STORAGE_KEY, next);
      } catch {
        // localStorage unavailable (private browsing) - view mode just won't persist
      }
      return next;
    });
  };

  // Keyboard shortcut: Ctrl+Shift+L toggles chat/journal view (visual only, never touches session state)
  useEffect(() => {
    const handleShortcut = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'L' || e.key === 'l')) {
        e.preventDefault();
        toggleViewMode();
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  // Narrow viewport detection for mobile drawer (breakpoint 768px)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsNarrowViewport(mq.matches);
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Real-time countdown timer - only for 3am AI chats (not journal, not ended)
  useEffect(() => {
    if (remainingTime > 0 && !journalMode && !sessionEnded && !isJournalMode) {
      const interval = setInterval(() => {
        setRemainingTime(prev => {
          const newTime = Math.max(0, prev - 1);
          if (newTime === 0 && !journalMode) {
            setJournalMode(true);
          }
          return newTime;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [remainingTime, journalMode, sessionEnded, isJournalMode]);

  // When timer runs out with an active AI session, end the session and mark complete (user can start new journal via +)
  const autoEndTriggeredRef = useRef(false);
  useEffect(() => {
    if (sessionId) autoEndTriggeredRef.current = false;
  }, [sessionId]);
  useEffect(() => {
    if (
      remainingTime === 0 &&
      sessionId &&
      userId &&
      !sessionEnded &&
      !isJournalMode &&
      !autoEndTriggeredRef.current
    ) {
      autoEndTriggeredRef.current = true;
      handleEndChat();
    }
  }, [remainingTime, sessionId, userId, sessionEnded, isJournalMode]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);
  
  // Auto-resize textarea as user types
  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  }, [message]);
  
  // Track scroll position for scroll-to-bottom button
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      setShowScrollButton(!isNearBottom && messages.length > 0);
    }
  };
  
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const loadDailyUsage = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      const response = await fetch(DAILY_USAGE_ENDPOINT, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'X-User-Date': getLocalDateString()
        }
      });

      if (!response.ok) {
        console.error('Failed to load daily usage');
        return false;
      }

      const data = await response.json();
      const usageData = data.statusCode ? JSON.parse(data.body) : data;

      setRemainingTime(usageData.remaining_time_seconds ?? 0);

      if (usageData.limit_reached) {
        setJournalMode(true);
      }
      return true;
    } catch (err) {
      console.error('Error loading daily usage:', err);
      return false;
    }
  };

  const loadStreaks = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      const response = await fetch(STREAKS_ENDPOINT, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'X-User-Date': getLocalDateString(),
          'X-User-Timezone-Offset': String(-new Date().getTimezoneOffset())
        }
      });
      if (!response.ok) return;
      const data = await response.json();
      const streaksData = data.statusCode ? JSON.parse(data.body) : data;
      setCurrentStreak(streaksData.current_streak ?? 0);
      setLongestStreak(streaksData.longest_streak ?? 0);
    } catch (err) {
      console.error('Error loading streaks:', err);
    }
  };

  const loadUserData = async () => {
    try {
      const user = await getCurrentUser();
      setUserId(user.userId);

      // Show cached chat list immediately if available, then refresh in background
      const cachedChats = getChatSessionsCache(user.userId);
      if (cachedChats && cachedChats.length >= 0) {
        setChats(cachedChats);
      }

      // Load chats from API (updates state and cache on success)
      await loadChatSessions(user.userId);

      // Load user profile
      await loadProfile();

      // Load daily usage so timer shows correct remaining (not 45:00) after refresh
      let usageOk = await loadDailyUsage();
      if (!usageOk) {
        // Retry once after short delay (auth/session may not be ready immediately on refresh)
        await new Promise((r) => setTimeout(r, 500));
        await loadDailyUsage();
      }
      await loadStreaks();
    } catch (err) {
      console.error('Error loading user data:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer resets at end of local day (midnight). Returns human-readable "resets in X" message.
  const getResetsInMessage = () => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const secondsUntil = Math.floor((midnight - now) / 1000);
    const hours = Math.floor(secondsUntil / 3600);
    const mins = Math.floor((secondsUntil % 3600) / 60);
    if (hours >= 1) {
      return `Resets in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    return `Resets in ${mins} ${mins === 1 ? 'minute' : 'minutes'}`;
  };

  const loadChatSessions = async (userId) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const response = await fetch(SESSIONS_ENDPOINT, {
        method: 'GET',
        headers: {
          'Authorization': token
        }
      });

      if (!response.ok) {
        console.error('Failed to load chat sessions');
        return;
      }

      const data = await response.json();
      
      // Handle Lambda proxy response (statusCode wrapper)
      const sessionsData = data.statusCode ? JSON.parse(data.body) : data;
      
      // Convert API format to UI format
      const sessions = sessionsData.sessions || [];
      const chatList = sessions.map(s => ({
        id: s.id,
        title: s.title,
        timestamp: s.timestamp,
        messages: null,  // Messages loaded on demand
        is_complete: s.is_complete || false,
        is_3am_ai: s.is_3am_ai !== false  // Default true for backward compatibility
      }));
      setChats(chatList);
      if (userId) setChatSessionsCache(userId, chatList);
    } catch (err) {
      console.error('Error loading chat sessions:', err);
    }
  };

  const loadProfile = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const response = await fetch(PROFILE_ENDPOINT, {
        method: 'GET',
        headers: {
          'Authorization': token
        }
      });

      if (!response.ok) {
        console.error('Failed to load profile, status:', response.status);
        return;
      }

      const data = await response.json();
      
      // Handle Lambda proxy response (statusCode wrapper)
      const profile = data.statusCode ? JSON.parse(data.body) : data;
      
      setUserProfile(profile);

      // Priority logic:
      // 1. If profile not completed, show onboarding (handles terms acceptance)
      // 2. If profile completed but terms not accepted, show terms modal
      if (!profile.profile_completed && !showSettings) {
        setShowOnboarding(true);
        setShowTermsModal(false);
      } else if (profile.profile_completed && profile.terms_accepted === false) {
        setShowTermsModal(true);
        setShowOnboarding(false);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    loadProfile(); // Reload profile after completion
  };

  const handleTermsAccept = () => {
    setShowTermsModal(false);
    loadProfile(); // Reload profile after acceptance
  };

  const loadSessionMessages = async (sessionId, chatMeta) => {
    if (isNarrowViewport) setSidebarOpenOnMobile(false);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const response = await fetch(`${SESSIONS_ENDPOINT}/${sessionId}`, {
        method: 'GET',
        headers: {
          'Authorization': token
        }
      });

      if (!response.ok) {
        console.error('Failed to load session messages');
        return;
      }

      const data = await response.json();
      const sessionData = data.statusCode ? JSON.parse(data.body) : data;
      const loadedMessages = sessionData.messages || [];
      setMessages(loadedMessages);
      
      // Extract message IDs
      const ids = loadedMessages.map(msg => msg.id || null);
      setMessageIds(ids);
      
      // Prefer API response (is_complete, is_3am_ai) so ended state is correct after login/refresh
      const fromApi = typeof sessionData.is_complete === 'boolean' || typeof sessionData.is_3am_ai === 'boolean';
      const isComplete = fromApi ? (sessionData.is_complete === true) : ((chatMeta || chats.find(c => c.id === sessionId))?.is_complete === true);
      const is3amAi = fromApi ? (sessionData.is_3am_ai !== false) : ((chatMeta || chats.find(c => c.id === sessionId))?.is_3am_ai !== false);
      setSessionEnded(isComplete);
      setIsJournalMode(!is3amAi);
      setSessionImage(sessionData.image_status ? { status: sessionData.image_status, url: sessionData.image_url || null } : null);
      // Keep sidebar in sync with API so list shows ended state after login
      if (fromApi) {
        setChats(prev => prev.map(c => c.id === sessionId ? { ...c, is_complete: isComplete, is_3am_ai: is3amAi } : c));
      }

      setSessionId(sessionId);
      setActiveChat(sessionId);
    } catch (err) {
      console.error('Error loading session messages:', err);
    }
  };

  // Poll the session's reflection image while pending (journal session just ended, image generating async)
  useEffect(() => {
    if (!sessionImage || sessionImage.status !== 'pending' || !sessionId || !userId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled || attempts >= 8) return;
      attempts += 1;
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();
        const response = await fetch(`${SESSIONS_ENDPOINT}/${sessionId}`, {
          method: 'GET',
          headers: { 'Authorization': token }
        });
        if (response.ok) {
          const data = await response.json();
          const sessionData = data.statusCode ? JSON.parse(data.body) : data;
          if (sessionData.image_status && sessionData.image_status !== 'pending') {
            if (!cancelled) setSessionImage({ status: sessionData.image_status, url: sessionData.image_url || null });
            return;
          }
        }
      } catch (err) {
        console.error('Error polling journal image status:', err);
      }
      if (!cancelled) setTimeout(poll, 4000);
    };
    const timeoutId = setTimeout(poll, 4000);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [sessionImage, sessionId, userId]);

  const handleSettingsClose = (updated) => {
    setShowSettings(false);
    setShowOnboarding(false); // Ensure onboarding is also closed
    if (updated) {
      loadProfile(); // Reload profile if it was updated
    }
  };

  const handleSignOut = async () => {
    try {
      // Chats are already saved in localStorage, just sign out
      await signOut();
      onLogout();
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const sendMessage = async (userMessage) => {
    setLoading(true);
    setLastFailedMessage(null);
    
    try {
      // Get JWT token
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      
      // Send to API
      const requestBody = {
        message: userMessage,
        client_id: 'web-client',  // Placeholder, JWT token provides real user_id
        is_journal: isJournalMode  // Flag for journal-only mode
      };
      
      // Only send session_id if it's a real UUID (not a temp ID)
      if (sessionId && !sessionId.startsWith('temp-')) {
        requestBody.session_id = sessionId;
      }
      
      // Add timeout to fetch (2 minutes to handle Lambda processing)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 min timeout
      
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
          'X-User-Date': getLocalDateString()
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', response.status, errorText);
        throw new Error(`API returned ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      // Parse response if it's a string (Lambda returns body as JSON string)
      const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
      // Handle Lambda proxy response (statusCode + body wrapper)
      const responseData = parsedData && parsedData.statusCode ? JSON.parse(parsedData.body) : parsedData;
      
      // Check for error in response
      if (responseData.error) {
        throw new Error(responseData.error);
      }
      
      // Save session ID for next message
      if (responseData.session_id) {
        setSessionId(responseData.session_id);
      }
      
      // Handle journal mode (no AI response)
      if (responseData.journal_mode || isJournalMode) {
        // Update temp chat with real session_id so we can load it when switching back
        if (responseData.session_id) {
          setChats(prev => prev.map(chat =>
            chat.id.startsWith('temp-')
              ? { ...chat, id: responseData.session_id, is_complete: false, is_3am_ai: false }
              : chat
          ));
          if (activeChat && activeChat.startsWith('temp-')) {
            setActiveChat(responseData.session_id);
          }
        }
        return;
      }
      
      // Handle daily limit reached (old auto-journal behavior)
      if (responseData.daily_limit_reached) {
        setJournalMode(true);
        setRemainingTime(0);
      }
      
      // Update remaining time
      if (responseData.remaining_time_seconds !== undefined) {
        setRemainingTime(responseData.remaining_time_seconds);
      }
      
      // Add assistant response with message ID
      if (responseData.response) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: responseData.response,
          id: responseData.message_id 
        }]);
        
        // Track message IDs (null for user msg, ID for assistant)
        setMessageIds(prev => [...prev, null, responseData.message_id]);
      }
      
      // Update chat list after first response (update temp chat with real session ID)
      setChats(prev => prev.map(chat => 
        chat.id.startsWith('temp-')
          ? { 
              ...chat, 
              id: responseData.session_id,
              is_complete: false,
              is_3am_ai: true  // We got an AI response, so it's 3am AI
            }
          : chat
      ));
      
      // Update active chat to real session ID if it was temp
      if (activeChat && activeChat.startsWith('temp-')) {
        setActiveChat(responseData.session_id);
      }
      
      // Check if session ended due to time limit
      if (responseData.session_ended) {
        // Trigger session end summary
        handleAutoSessionEnd(responseData.session_id);
      }
      
      // Note: Messages are stored in database via Lambda, no need to update chats array
    } catch (err) {
      console.error('Send error:', err);
      
      let errorMessage = 'Failed to send message. Please try again.';
      
      if (err.name === 'AbortError') {
        errorMessage = 'Request timed out. The server is taking too long to respond.';
      } else if (err.message && err.message.includes('Failed to fetch')) {
        // "Failed to fetch" often means: CORS block, auth rejected (no CORS on 401), or connection issue.
        errorMessage = 'Network error. This can happen if your session expired or the server is unreachable. Try refreshing the page and signing in again, or check your connection.';
      } else if (err.message && err.message.includes('504')) {
        errorMessage = 'The server took too long to respond. Please try again in a moment.';
      } else {
        errorMessage = err.message;
      }
      
      setMessages(prev => [...prev, { 
        role: 'error', 
        content: errorMessage 
      }]);
      setLastFailedMessage(userMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim() || loading) return;
    
    const userMessage = message;
    setMessage('');
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    // Create chat entry immediately on first message
    if (messages.length === 0) {
      const tempSessionId = `temp-${Date.now()}`;
      const newChat = {
        id: tempSessionId,
        title: userMessage.slice(0, 50) + (userMessage.length > 50 ? '...' : ''),
        timestamp: new Date().toISOString(),
        messages: [{ role: 'user', content: userMessage }]
      };
      setChats(prev => [newChat, ...prev]);
      setActiveChat(tempSessionId);
    }
    
    await sendMessage(userMessage);
    
    // Refocus textarea after send - defer to next tick so it happens after React re-render
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleRetry = () => {
    if (!lastFailedMessage) return;
    
    // Remove error message
    setMessages(prev => prev.filter(msg => msg.role !== 'error'));
    
    sendMessage(lastFailedMessage);
  };

  const handleEndChat = async () => {
    if (!sessionId || !userId) {
      // Clear state if no session
      setMessages([]);
      setMessageIds([]);
      setSessionId(null);
      setActiveChat(null);
      setMessageFeedback({});
      return;
    }

    try {
      // Call API to generate summary
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const response = await fetch(SESSION_END_ENDPOINT(sessionId), {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const summaryData = data.statusCode ? JSON.parse(data.body) : data;
        
        // Show farewell/analysis message in chat
        if (summaryData.farewell_message) {
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: summaryData.farewell_message
          }]);
        }
      }
    } catch (err) {
      console.error('Error ending session:', err);
    }
    
    // Journal session ending kicks off async image generation server-side; show "drawing..." right away
    if (isJournalMode && messages.length > 0) {
      setSessionImage({ status: 'pending', url: null });
    }

    // Mark session as ended (keep messages visible but disable input)
    setSessionEnded(true);
    setIsJournalMode(false);
    setLastFailedMessage(null);
    setMessageFeedback({});
    setSidebarCollapsed(false);

    // Optimistically mark this chat as ended in sidebar so it stays ended
    if (sessionId) {
      setChats(prev => prev.map(c => c.id === sessionId ? { ...c, is_complete: true } : c));
    }

    // Reload sessions so list has is_complete from server (ended chats stay ended)
    await loadChatSessions(userId);

    // Re-apply ended state for this session so a stale server response doesn't overwrite it
    if (sessionId) {
      setChats(prev => prev.map(c => c.id === sessionId ? { ...c, is_complete: true } : c));
    }

    // Reload daily usage to ensure timer reflects updated remaining time
    await loadDailyUsage();
    await loadStreaks();
  };

  const handleAutoSessionEnd = async (sid) => {
    // Auto-triggered when time limit reached
    if (!sid || !userId) return;

    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const response = await fetch(SESSION_END_ENDPOINT(sid), {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const summaryData = data.statusCode ? JSON.parse(data.body) : data;
        
        // Show farewell/analysis message in chat
        if (summaryData.farewell_message) {
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: summaryData.farewell_message
          }]);
        }
      }
    } catch (err) {
      console.error('Error auto-ending session:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFeedback = async (messageIndex, liked) => {
    // liked: true=like, false=dislike, null=unselect/toggle off
    
    // Get the real message ID from the messages array
    const msg = messages[messageIndex];
    const messageId = msg?.id;
    
    // Only allow feedback on assistant messages with IDs
    if (!messageId) {
      console.warn('Cannot provide feedback: message ID not available');
      return;
    }
    
    // Update local state (null = cleared/unselected)
    setMessageFeedback(prev => ({
      ...prev,
      [messageIndex]: liked === null
        ? undefined  // Remove entry when unselected
        : {
            liked,
            showTextBox: liked === false,  // Show text box only after dislike
            feedbackText: prev[messageIndex]?.feedbackText || ''
          }
    }));

    // Persist to backend: like (immediate), dislike (via submit), or clear (liked=null)
    if (liked === true) {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();
        
        const response = await fetch(FEEDBACK_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            message_id: messageId,
            liked: true,
            feedback_text: null
          })
        });
        await response.json();
      } catch (err) {
        console.error('Error saving feedback:', err);
      }
    } else if (liked === null) {
      // Clear/unselect - call API to remove feedback
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();
        await fetch(FEEDBACK_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            message_id: messageId,
            liked: null,
            feedback_text: null
          })
        });
      } catch (err) {
        console.error('Error clearing feedback:', err);
      }
    }
  };

  const handleFeedbackSubmit = async (messageIndex) => {
    const feedback = messageFeedback[messageIndex];
    if (!feedback) return;

    // Get the real message ID
    const msg = messages[messageIndex];
    const messageId = msg?.id;
    
    if (!messageId) {
      console.warn('Cannot submit feedback: message ID not available');
      return;
    }

    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      
      const response = await fetch(FEEDBACK_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          message_id: messageId,
          liked: false,
          feedback_text: feedback.feedbackText || null
        })
      });

      // Hide text box after submission
      setMessageFeedback(prev => ({
        ...prev,
        [messageIndex]: {
          ...prev[messageIndex],
          showTextBox: false
        }
      }));
    } catch (err) {
      console.error('Error saving feedback:', err);
    }
  };

  const handleFeedbackTextChange = (messageIndex, text) => {
    setMessageFeedback(prev => ({
      ...prev,
      [messageIndex]: {
        ...prev[messageIndex],
        feedbackText: text
      }
    }));
  };

  const handlePlusButtonClick = () => {
    // If there's an active session with messages, show confirmation first
    if (sessionId && messages.length > 0 && !sessionEnded) {
      setShowEndChatConfirm(true);
    } else {
      // No active session, show dropdown directly
      setShowNewChatDropdown(true);
    }
  };

  const handleNewAIChat = async () => {
    // Refetch daily usage so timer = 90 min total for the day minus time already used (all chats)
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      const res = await fetch(DAILY_USAGE_ENDPOINT, {
        method: 'GET',
        headers: { 'Authorization': token, 'X-User-Date': getLocalDateString() }
      });
      if (res.ok) {
        const data = await res.json();
        const usageData = data.statusCode ? JSON.parse(data.body) : data;
        const remaining = usageData.remaining_time_seconds ?? 0;
        const limitReached = usageData.limit_reached === true;
        setRemainingTime(remaining);
        if (limitReached) setJournalMode(true);
        if (remaining <= 0) {
          alert('Daily AI chat limit reached. You can start a journal session instead.');
          return;
        }
      }
    } catch (err) {
      console.error('Error loading daily usage for new chat:', err);
    }

    // Start new AI chat session
    setMessages([]);
    setMessageIds([]);
    setSessionId(null);
    setActiveChat(null);
    setIsJournalMode(false);
    setSessionEnded(false);
    setLastFailedMessage(null);
    setMessageFeedback({});
    setJournalMode(false);
    setSidebarCollapsed(true);
    setSessionImage(null);
  };

  const handleNewJournal = () => {
    // Start new journal session (always allowed, unlimited)
    setMessages([]);
    setMessageIds([]);
    setSessionId(null);
    setActiveChat(null);
    setIsJournalMode(true);
    setSessionEnded(false);
    setLastFailedMessage(null);
    setMessageFeedback({});
    setJournalMode(false);
    setSidebarCollapsed(true);
    setSessionImage(null);
  };

  const handleEndChatConfirmed = async () => {
    setShowEndChatConfirm(false);
    
    // End the current session first
    await handleEndChat();
    
    // Execute the pending action if any
    if (pendingAction === 'ai-chat') {
      handleNewAIChat();
    } else if (pendingAction === 'journal') {
      handleNewJournal();
    }
    
    setPendingAction(null);
  };

  const handleConvertToAI = async () => {
    if (!sessionId) return;
    
    // Check daily limit
    if (remainingTime <= 0) {
      alert('Daily AI chat limit reached. Cannot convert to AI chat.');
      return;
    }
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      
      const response = await fetch(`${SESSIONS_ENDPOINT}/${sessionId}/convert-to-ai`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
          'X-User-Date': getLocalDateString()
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to convert to AI chat');
      }
      
      const data = await response.json();
      const convertData = data.statusCode ? JSON.parse(data.body) : data;
      
      // Update state to AI mode
      setIsJournalMode(false);
      setRemainingTime(convertData.remaining_time_seconds || remainingTime);
      
      // Reload chat sessions to update the flag in sidebar
      await loadChatSessions(userId);
    } catch (err) {
      console.error('Error converting to AI:', err);
      alert(err.message || 'Failed to convert to AI chat');
    }
  };

  return (
    <div className={`chat-container mode-${viewMode}`}>
      {showTermsModal && (
        <TermsAcceptanceModal onAccept={handleTermsAccept} />
      )}
      
      {showOnboarding && !showTermsModal && (
        <OnboardingModal onComplete={handleOnboardingComplete} />
      )}
      
      {showSettings && (
        <ProfileSettings 
          onClose={handleSettingsClose} 
          initialProfile={userProfile}
        />
      )}

      {showEndChatConfirm && (
        <EndChatConfirmModal 
          onCancel={() => {
            setShowEndChatConfirm(false);
            setPendingAction(null);
          }}
          onConfirm={handleEndChatConfirmed}
          isJournalMode={isJournalMode}
        />
      )}

      {isNarrowViewport && sidebarOpenOnMobile && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpenOnMobile(false)}
          aria-hidden="true"
        />
      )}
      <div className={`sidebar ${!isNarrowViewport && sidebarCollapsed ? 'collapsed' : ''} ${isNarrowViewport && sidebarOpenOnMobile ? 'open-mobile' : ''}`}>
        {(!sidebarCollapsed || (isNarrowViewport && sidebarOpenOnMobile)) && (
          <>
            <div className="sidebar-header">
              <div className="sidebar-logo">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/logo.png`}
                  alt=""
                  className="sidebar-lotus-logo"
                  width={28}
                  height={28}
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.add('visible'); }}
                />
                <span className="sidebar-logo-fallback" aria-hidden>3</span>
                <h2>3AM AI</h2>
              </div>
            </div>
        <div className="sidebar-content">
          <div className="sidebar-section">
            <div className="section-header" onClick={() => setShowAnalysis(!showAnalysis)}>
              <h3>Life Analysis</h3>
              <span className="toggle">{showAnalysis ? '−' : '+'}</span>
            </div>
            {showAnalysis && (
              <div className="section-content life-analysis-links">
                {LIFE_ANALYSIS_SECTIONS.map(({ id, label }) =>
                  id === 'mood' ? (
                    <div key={id} className="life-analysis-link-row">
                      <Link to={`/analysis?tab=${id}`} className="life-analysis-link" onClick={() => isNarrowViewport && setSidebarOpenOnMobile(false)}>
                        <span className="life-analysis-link-icon">{LifeAnalysisIcons[id]}</span>
                        {label}
                      </Link>
                      <Link to="/analysis?tab=mood&track=1" className="life-analysis-mood-plus" title="Track mood" aria-label="Track mood" onClick={() => isNarrowViewport && setSidebarOpenOnMobile(false)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor" />
                        </svg>
                      </Link>
                    </div>
                  ) : (
                    <Link key={id} to={`/analysis?tab=${id}`} className="life-analysis-link" onClick={() => isNarrowViewport && setSidebarOpenOnMobile(false)}>
                      <span className="life-analysis-link-icon">{LifeAnalysisIcons[id]}</span>
                      {label}
                    </Link>
                  )
                )}
              </div>
            )}
          </div>
          
          <div className="sidebar-section sidebar-section-chats">
            <div className="section-header section-header-chats">
              <div className="chats-header-left" onClick={() => setShowChats(!showChats)}>
                <h3>Your chats</h3>
                <span
                  className="streaks-badge"
                  title={currentStreak === 0
                    ? 'Start a new streak now'
                    : 'You are prioritising your mental health. Keep it up!'}
                >
                  🔥 {currentStreak}
                </span>
                <button 
                  ref={newChatButtonRef}
                  className="new-chat-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlusButtonClick();
                  }}
                  title="Start new chat or journal"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                  </svg>
                </button>
                {showNewChatDropdown && (
                  <NewChatDropdown 
                    show={showNewChatDropdown}
                    onClose={() => setShowNewChatDropdown(false)}
                    onSelectAIChat={() => {
                      if (sessionId && messages.length > 0 && !sessionEnded) {
                        setPendingAction('ai-chat');
                        setShowEndChatConfirm(true);
                      } else {
                        handleNewAIChat();
                      }
                    }}
                    onSelectJournal={() => {
                      if (sessionId && messages.length > 0 && !sessionEnded) {
                        setPendingAction('journal');
                        setShowEndChatConfirm(true);
                      } else {
                        handleNewJournal();
                      }
                    }}
                    dailyLimitReached={remainingTime <= 0}
                    resetsInMessage={remainingTime <= 0 ? getResetsInMessage() : null}
                  />
                )}
              </div>
              <span className="toggle" onClick={() => setShowChats(!showChats)}>{showChats ? '−' : '+'}</span>
            </div>
            {showChats && (
              <div className="section-content section-content-chats">
                {chats.length === 0 ? (
                  <div className="empty-chats">No chats yet</div>
                ) : (
                  <>
                    {(chats.length <= 5 ? chats : chats.slice(0, 5)).map(chat => (
                      <div 
                        key={chat.id} 
                        className={`chat-item ${activeChat === chat.id ? 'active' : ''} ${chat.is_complete ? 'ended' : ''}`}
                        onClick={() => loadSessionMessages(chat.id, chat)}
                      >
                        <div className="chat-item-content">
                          {chat.is_3am_ai ? (
                            <span className="chat-item-3am-label">3AM</span>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="journal-icon" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 19l7-7 3 3-7 7-3-3z" />
                              <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
                            </svg>
                          )}
                          <div className="chat-item-text">
                            <span className="chat-item-title">{chat.title}</span>
                            <span className="chat-item-date">{formatChatDate(chat.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {chats.length > 5 && !showOlderChats && (
                      <button
                        type="button"
                        className="show-older-chats-btn"
                        onClick={() => setShowOlderChats(true)}
                      >
                        Older chats
                      </button>
                    )}
                    {chats.length > 5 && showOlderChats && (
                      <div className="older-chats-box">
                        <div className="older-chats-header">
                          <span className="older-chats-label">Older chats</span>
                          <button
                            type="button"
                            className="older-chats-collapse-btn"
                            onClick={() => setShowOlderChats(false)}
                            title="Collapse older chats"
                            aria-label="Collapse older chats"
                          >
                            −
                          </button>
                        </div>
                        {chats.slice(5).map(chat => (
                          <div 
                            key={chat.id} 
                            className={`chat-item ${activeChat === chat.id ? 'active' : ''} ${chat.is_complete ? 'ended' : ''}`}
                            onClick={() => loadSessionMessages(chat.id, chat)}
                          >
                            <div className="chat-item-content">
                              {chat.is_3am_ai ? (
                                <span className="chat-item-3am-label">3AM</span>
                              ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="journal-icon" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 19l7-7 3 3-7 7-3-3z" />
                                  <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
                                </svg>
                              )}
                              <div className="chat-item-text">
                                <span className="chat-item-title">{chat.title}</span>
                                <span className="chat-item-date">{formatChatDate(chat.timestamp)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
            <div className="sidebar-footer">
              <button
                className="settings-icon-btn footer-settings-btn"
                onClick={() => setShowSettings(true)}
                title="Settings"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22l-1.92 3.32c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" fill="currentColor"/>
                </svg>
              </button>
              <button onClick={handleSignOut} className="logout-btn">
                Log out
              </button>
            </div>
          </>
        )}
        <button 
          className="sidebar-toggle" 
          onClick={() => isNarrowViewport ? setSidebarOpenOnMobile(false) : setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="currentColor"/>
          </svg>
        </button>
      </div>
      <div className="chat-main">
        <button
          type="button"
          className="mobile-menu-btn"
          onClick={() => setSidebarOpenOnMobile(true)}
          title="Open menu"
          aria-label="Open menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="currentColor"/>
          </svg>
        </button>
        <div className="view-toggle-bar">
          <button
            type="button"
            className="view-toggle-btn"
            onClick={toggleViewMode}
            title={`Switch to ${viewMode === 'chat' ? 'journal' : 'chat'} view (Ctrl+Shift+L)`}
            aria-label="Toggle chat/journal view"
          >
            {viewMode === 'chat' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 19l7-7 3 3-7 7-3-3z" />
                <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M8 12h8M8 16h5M21 15c0 1.1-.9 2-2 2h-3l-4 4v-4H5c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2v9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            <span className="view-toggle-label">{viewMode === 'chat' ? 'Journal view' : 'Chat view'}</span>
          </button>
        </div>
        {messages.length > 0 && (
          <div className="chat-header">
            {!sessionEnded && !isJournalMode && messages.some(m => m.role === 'assistant') && (
              <>
                <div className={`timer-display ${remainingTime < 300 && remainingTime > 0 ? 'timer-warning' : ''}`}>
                  {formatTime(remainingTime)} remaining today
                </div>
                <button onClick={handleEndChat} className="end-chat-btn" title="End chat">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
                </svg>
                End chat
              </button>
              </>
            )}
            {!sessionEnded && isJournalMode && (
              <button onClick={handleEndChat} className="end-chat-btn" title="End journaling">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
                </svg>
                End journaling
              </button>
            )}
          </div>
        )}
        {remainingTime < 300 && remainingTime > 0 && !journalMode && !isJournalMode && messages.some(m => m.role === 'assistant') && (
          <div className="time-warning-banner">
            Less than 5 minutes remaining for today
          </div>
        )}
        {isJournalMode && !sessionEnded && (
          <div className="journal-mode-banner">
            <div className="banner-content">
              <span>Journal Mode - Your thoughts are being saved (no AI responses)</span>
              {remainingTime > 0 && (
                <button className="convert-to-ai-btn" onClick={handleConvertToAI}>
                  Chat with 3AM
                </button>
              )}
            </div>
          </div>
        )}
        {journalMode && (
          <div className="journal-mode-banner">
            Daily chat limit reached. Continue journaling your thoughts - they'll be saved securely.
          </div>
        )}
        <div className="chat-messages" ref={messagesContainerRef} onScroll={handleScroll}>
          {messages.length === 0 ? (
            <div className="empty-state">
              <h1>Your safe space to talk or journal.<br />I listen and ask questions, then we reflect.</h1>
            </div>
          ) : (
            <div className={`messages-list ${viewMode === 'journal' ? 'journal-entries-list' : ''}`}>
              {messages.map((msg, idx) => (
                viewMode === 'journal' && msg.role !== 'error' ? (
                  <JournalEntryCard
                    key={idx}
                    role={msg.role}
                    content={msg.content}
                    image={idx === messages.length - 1 ? sessionImage : null}
                  />
                ) : (
                <div key={idx} className={`message ${msg.role}`}>
                  <div className="message-content">{msg.content}</div>
                  {msg.role === 'error' && (
                    <div className="error-actions">
                      <button onClick={handleRetry} className="retry-btn">
                        Retry
                      </button>
                      <button onClick={() => window.location.reload()} className="refresh-btn">
                        Refresh page
                      </button>
                    </div>
                  )}
                  {msg.role === 'assistant' && !sessionEnded && (
                    <div className="feedback-buttons">
                      <button 
                        className={`feedback-btn ${messageFeedback[idx]?.liked === true ? 'active' : ''}`}
                        onClick={() => {
                          const current = messageFeedback[idx]?.liked;
                          handleFeedback(idx, current === true ? null : true);
                        }}
                        title="Like (click again to unselect)"
                      >
                        👍
                      </button>
                      <button 
                        className={`feedback-btn ${messageFeedback[idx]?.liked === false ? 'active' : ''}`}
                        onClick={() => {
                          const current = messageFeedback[idx]?.liked;
                          handleFeedback(idx, current === false ? null : false);
                        }}
                        title="Dislike (click again to unselect)"
                      >
                        👎
                      </button>
                      {messageFeedback[idx]?.showTextBox && (
                        <div className="feedback-text-box">
                          <textarea
                            placeholder="What could be improved?"
                            value={messageFeedback[idx]?.feedbackText || ''}
                            onChange={(e) => handleFeedbackTextChange(idx, e.target.value)}
                            rows="3"
                          />
                          <button 
                            className="feedback-submit-btn"
                            onClick={() => handleFeedbackSubmit(idx)}
                          >
                            Submit feedback
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )
              ))}
              {loading && (
                <div className="message assistant">
                  <div className="message-content typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
          {showScrollButton && (
            <button className="scroll-to-bottom" onClick={scrollToBottom} title="Scroll to bottom">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M7 10l5 5 5-5z" fill="currentColor"/>
              </svg>
            </button>
          )}
        </div>
        {sessionEnded && (
          <div className="session-ended-banner">
            This chat has ended. Start a new chat to continue.
          </div>
        )}
        {!sessionEnded && (
          <>
            <div className="chat-disclaimer">
              3AM AI is not a therapist and it can make mistakes
            </div>
            <div className="chat-input-container">
              <div className="chat-input-wrapper">
                <textarea
                  ref={textareaRef}
                  className="chat-input"
                  placeholder="What's on your mind?"
                  rows="1"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                />
                <button className="send-btn" onClick={handleSend} disabled={!message.trim() || loading}>
                  Send
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ChatLayout;
